import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiUrl } from '../../config';

interface AITabSummaryProps {
  caseId: string;
  tabName: 'progress' | 'activity' | 'notes' | 'patient-info' | 'documents';
  tabData?: any; // Optional: specific data for the tab
  patientPII?: any; // Patient information
}

export function AITabSummary({ caseId, tabName, tabData, patientPII }: AITabSummaryProps) {
  const { t, i18n } = useTranslation();
  const [summary, setSummary] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(true); // Open by default
  const [isContentExpanded, setIsContentExpanded] = useState(false); // For text expansion (starts at 3 rows)

  useEffect(() => {
    fetchSummary();
  }, [caseId, tabName, tabData]);

  const fetchSummary = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const language = i18n.language === 'he' ? 'he' : 'en';

      // Build context based on tab
      const tabContext = getTabContext(tabName, tabData, patientPII);

      const apiUrl = getApiUrl('/api/ai/summarize');

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          content: tabContext,
          tabName,
          language,
          provider: 'ollama',  // Use Ollama (which runs OpenChat model)
          model: 'openchat:latest'  // Request OpenChat model for summarization
        }),
      });

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      const data = await response.json();
      setSummary(data.summary || '');
    } catch (err: any) {
      console.error('Error fetching summary:', err);
      setError(err?.message || 'Failed to load summary');
    } finally {
      setIsLoading(false);
    }
  };

  const getTabContext = (tab: string, data: any, patient: any): string => {
    const language = i18n.language === 'he' ? 'he' : 'en';

    // Return actual data as JSON string for AI to summarize
    const contextData: any = {};

    if (patient) {
      contextData.patient = patient;
    }

    if (data) {
      contextData.tabData = data;
    }

    // Add tab-specific context
    switch (tab) {
      case 'progress':
        contextData.tabType = language === 'he' ? 'התקדמות ויעדים' : 'Progress and goals';
        break;
      case 'activity':
        contextData.tabType = language === 'he' ? 'פגישות ופעילויות' : 'Meetings and activities';
        break;
      case 'notes':
        contextData.tabType = language === 'he' ? 'טפסי קליטה' : 'Intake forms';
        break;
      case 'patient-info':
        contextData.tabType = language === 'he' ? 'מידע אישי' : 'Personal information';
        break;
      case 'documents':
        contextData.tabType = language === 'he' ? 'מסמכים' : 'Documents';
        break;
    }

    return JSON.stringify(contextData, null, 2);
  };

  const getTabNameHebrew = (tab: string): string => {
    switch (tab) {
      case 'progress': return 'התקדמות';
      case 'activity': return 'פגישות';
      case 'notes': return 'טפסי קליטה';
      case 'patient-info': return 'מידע אישי';
      case 'documents': return 'מסמכים';
      default: return tab;
    }
  };

  const getTabNameEnglish = (tab: string): string => {
    switch (tab) {
      case 'progress': return 'Progress';
      case 'activity': return 'Meetings';
      case 'notes': return 'Intake Forms';
      case 'patient-info': return 'Patient Information';
      case 'documents': return 'Documents';
      default: return tab;
    }
  };

  if (error && !summary) {
    return null; // Don't show error state, just fail silently
  }

  return (
    <div style={{
      marginBottom: '20px',
      border: '1px solid #e0e0e0',
      borderRadius: '8px',
      backgroundColor: '#f8f9fa',
      overflow: 'hidden'
    }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 16px',
          backgroundColor: '#007acc',
          color: 'white',
          cursor: 'pointer',
          userSelect: 'none'
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>🤖</span>
          <span style={{ fontWeight: '600', fontSize: '14px' }}>
            {i18n.language === 'he'
              ? `סיכום AI - ${getTabNameHebrew(tabName)}`
              : `AI Summary - ${getTabNameEnglish(tabName)}`
            }
          </span>
        </div>
        <button
          style={{
            background: 'none',
            border: 'none',
            color: 'white',
            fontSize: '18px',
            cursor: 'pointer',
            padding: '0',
            width: '24px',
            height: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
        >
          {isExpanded ? '▼' : '▶'}
        </button>
      </div>

      {isExpanded && (
        <div style={{ padding: '16px' }}>
          {isLoading ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: '#666',
              fontSize: '14px'
            }}>
              <div style={{
                width: '16px',
                height: '16px',
                border: '2px solid #007acc',
                borderTop: '2px solid transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
              {i18n.language === 'he' ? 'טוען סיכום...' : 'Loading summary...'}
            </div>
          ) : summary ? (
            <div>
              <div
                style={{
                  color: '#333',
                  fontSize: '14px',
                  lineHeight: '1.6',
                  whiteSpace: 'pre-wrap',
                  display: isContentExpanded ? 'block' : '-webkit-box',
                  WebkitLineClamp: isContentExpanded ? 'none' : 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: isContentExpanded ? 'visible' : 'hidden',
                  textOverflow: 'ellipsis',
                  maxHeight: isContentExpanded ? 'none' : '4.8em' // 3 lines * 1.6 line-height
                }}
              >
                {summary}
              </div>
              {(summary.length > 150 || summary.split('\n').length > 3) && (
                <button
                  onClick={() => setIsContentExpanded(!isContentExpanded)}
                  style={{
                    marginTop: '8px',
                    background: 'none',
                    border: 'none',
                    color: '#007acc',
                    cursor: 'pointer',
                    fontSize: '13px',
                    padding: '4px 0',
                    textDecoration: 'underline',
                    fontWeight: '500'
                  }}
                >
                  {isContentExpanded
                    ? (i18n.language === 'he' ? 'הצג פחות' : 'Show less')
                    : (i18n.language === 'he' ? 'הצג עוד' : 'Show more')
                  }
                </button>
              )}
            </div>
          ) : error ? (
            <div style={{ color: '#dc3545', fontSize: '14px' }}>
              {i18n.language === 'he'
                ? 'לא ניתן לטעון סיכום כרגע'
                : 'Unable to load summary at this time'
              }
            </div>
          ) : null}
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

